import {
  getCompanyKnowledgeContentBlob,
  getProjectKnowledgeContentBlob,
  linkCompanyKnowledge,
  linkProjectKnowledge,
  listCompanyKnowledge,
  listPortfolioFiles,
  listProjectKnowledge,
  listProjects,
  removeCompanyKnowledge,
  removeProjectKnowledge,
  uploadCompanyKnowledge,
  uploadProjectKnowledge,
  type CoworkKnowledge,
  type CoworkPortfolioFile,
  type CoworkProject,
} from "@/cowork/api"
import { useLanguage } from "@/context/language"
import { trialErrorKey } from "@/cowork/trial-errors"
import { KnowledgeGuidance } from "@/components/knowledge-guidance"
import { FileIcon } from "@opencode-ai/ui/file-icon"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { createEffect, createMemo, createResource, For, on, onCleanup, Show } from "solid-js"
import { createStore } from "solid-js/store"

type KnowledgeScope = Readonly<{ projectID: string | null; companyID: string; token: number }>
type KnowledgeReceipt = { knowledgeID: string; item?: CoworkKnowledge; revision: number }
type UploadItem = {
  id: number
  file: File
  scope: KnowledgeScope
  status: "pending" | "error"
}

export function CoworkKnowledgePage(props: {
  projectID?: string
  companyID: string
  canEdit: boolean
  onChanged?: (scope: { companyID: string; projectID: string | null }) => void
}) {
  const language = useLanguage()
  let generation = 0
  const scope = createMemo(() =>
    Object.freeze({ projectID: props.projectID ?? null, companyID: props.companyID, token: ++generation }),
  )
  const [state, setState] = createStore({
    error: "",
    addOpen: false,
    linking: "",
    removing: "",
    removeTarget: undefined as CoworkKnowledge | undefined,
  })
  const [uploads, setUploads] = createStore<UploadItem[]>([])
  const [receipts, setReceipts] = createStore({ token: scope().token, items: [] as KnowledgeReceipt[] })
  let revision = 0
  const pendingUploads = new Set<number>()
  let nextUploadID = 0
  let picker: HTMLInputElement | undefined
  let pickerScope: KnowledgeScope | undefined
  let addDialog: HTMLElement | undefined
  let addDialogTrigger: HTMLElement | undefined
  let primaryAction: HTMLButtonElement | undefined
  let knowledgeHeading: HTMLHeadingElement | undefined
  let removeDialog: HTMLElement | undefined
  let removeDialogTrigger: HTMLElement | undefined
  let alive = true
  onCleanup(() => {
    alive = false
  })
  const isCurrent = (selected: KnowledgeScope) => alive && scope().token === selected.token

  const [knowledge, knowledgeActions] = createResource(scope, async (selected) => ({
    scope: selected,
    revision,
    items: await (selected.projectID === null
      ? listCompanyKnowledge(selected.companyID)
      : listProjectKnowledge(selected.projectID, selected.companyID)),
  }))
  const dialogScope = createMemo(() => (state.addOpen ? scope() : undefined))
  const [projectFiles, projectFileActions] = createResource(dialogScope, async (selected) => {
    const [items, projects] = await Promise.all([
      listPortfolioFiles({
        entityID: selected.companyID,
        projectID: selected.projectID ?? undefined,
        companyID: selected.companyID,
      }),
      selected.projectID === null ? listProjects(selected.companyID) : Promise.resolve([]),
    ])
    return { scope: selected, items, projects }
  })
  const knowledgeItems = createMemo(() => {
    const selected = scope()
    const value = knowledge.state === "ready" || knowledge.state === "refreshing" ? knowledge.latest : undefined
    let items =
      value?.scope.token === selected.token ? value.items.filter((item) => knowledgeAvailableInScope(item, selected)) : []
    if (receipts.token !== selected.token) return items
    for (const receipt of receipts.items) {
      items = receipt.item ? upsertKnowledgeItem(items, receipt.item) : removeKnowledgeItem(items, receipt.knowledgeID)
    }
    return items
  })
  createEffect(() => {
    if (knowledge.state !== "ready") return
    const value = knowledge.latest
    if (!value || !isCurrent(value.scope) || receipts.token !== value.scope.token) return
    // Only a read started after the acknowledgment can retire its receipt.
    setReceipts("items", (items) =>
      items.filter((receipt) => {
        if (receipt.revision > value.revision) return true
        const present = value.items.some(
          (item) => item.knowledge_id === receipt.knowledgeID && knowledgeMatchesScope(item, value.scope),
        )
        return receipt.item ? !present : present
      }),
    )
  })
  const availableFiles = createMemo(() => {
    if (projectFiles.state !== "ready" && projectFiles.state !== "refreshing") return []
    const value = projectFiles.latest
    if (value?.scope.token !== dialogScope()?.token) return []
    return value.items.filter(
      (file) =>
        isKnowledgeCandidate(file, value.scope, value.projects) &&
        !knowledgeItems().some((item) => item.file_id === file.id),
    )
  })

  createEffect(
    on(scope, () => {
      setState({ error: "", addOpen: false, linking: "", removing: "", removeTarget: undefined })
      setUploads([])
      setReceipts({ token: scope().token, items: [] })
    }),
  )
  createEffect(() => {
    if (state.addOpen) queueMicrotask(() => focusFirst(addDialog))
  })
  createEffect(() => {
    if (state.removeTarget) queueMicrotask(() => focusFirst(removeDialog))
  })

  const closeAddDialog = () => {
    setState("addOpen", false)
    queueMicrotask(() => addDialogTrigger?.focus())
  }

  const closeRemoveDialog = () => {
    if (state.removing) return
    setState("removeTarget", undefined)
    queueMicrotask(() => removeDialogTrigger?.focus())
  }

  const acknowledge = (selected: KnowledgeScope, knowledgeID: string, item?: CoworkKnowledge) => {
    if (!isCurrent(selected)) return
    setReceipts({
      token: selected.token,
      items: [
        ...receipts.items.filter((receipt) => receipt.knowledgeID !== knowledgeID),
        { knowledgeID, item, revision: ++revision },
      ],
    })
    // Refresh in the background; confirmed writes remain visible even if this read is stale or fails.
    void knowledgeActions.refetch()
    props.onChanged?.({ companyID: selected.companyID, projectID: selected.projectID })
  }

  const removeAvailableFile = (selected: KnowledgeScope, fileID: string) => {
    if (!isCurrent(selected)) return
    projectFileActions.mutate((value) =>
      value?.scope.token === selected.token
        ? { ...value, items: value.items.filter((file) => file.id !== fileID) }
        : value,
    )
  }

  const focusKnowledgePrimary = () => {
    queueMicrotask(() => queueMicrotask(() => (props.canEdit ? primaryAction : knowledgeHeading)?.focus()))
  }

  const submitUpload = async (item: UploadItem) => {
    if (
      !isCurrent(item.scope) ||
      !props.canEdit ||
      pendingUploads.has(item.id) ||
      !uploads.some((entry) => entry.id === item.id)
    )
      return
    pendingUploads.add(item.id)
    setUploads((entry) => entry.id === item.id, "status", "pending")
    try {
      const result = await (item.scope.projectID === null
        ? uploadCompanyKnowledge(item.scope.companyID, item.file)
        : uploadProjectKnowledge(item.scope.projectID, item.file, item.scope.companyID))
      if (!isCurrent(item.scope) || !uploads.some((entry) => entry.id === item.id)) return
      if (!result?.knowledge_id || !result.file_id || !knowledgeMatchesScope(result, item.scope)) {
        setUploads((entry) => entry.id === item.id, "status", "error")
        return
      }
      setUploads((items) => items.filter((entry) => entry.id !== item.id))
      acknowledge(item.scope, result.knowledge_id, result)
      removeAvailableFile(item.scope, result.file_id)
    } catch (error) {
      const key = trialErrorKey(error)
      if (key && isCurrent(item.scope)) setState("error", language.t(key))
      if (isCurrent(item.scope) && uploads.some((entry) => entry.id === item.id))
        setUploads((entry) => entry.id === item.id, "status", "error")
    } finally {
      pendingUploads.delete(item.id)
    }
  }

  const download = async (item: CoworkKnowledge) => {
    const selected = scope()
    if (!isCurrent(selected) || !knowledgeAvailableInScope(item, selected)) return
    setState("error", "")
    try {
      const blob = await (selected.projectID === null
        ? getCompanyKnowledgeContentBlob(selected.companyID, item.knowledge_id)
        : getProjectKnowledgeContentBlob(selected.projectID, item.knowledge_id, selected.companyID))
      if (!isCurrent(selected)) return
      const url = URL.createObjectURL(blob)
      try {
        const anchor = document.createElement("a")
        anchor.href = url
        anchor.download = item.filename
        anchor.click()
      } finally {
        URL.revokeObjectURL(url)
      }
    } catch {
      if (isCurrent(selected)) setState("error", language.t("cowork.enterprise.knowledge.downloadError"))
    }
  }

  const linkFile = async (file: CoworkPortfolioFile) => {
    const selected = scope()
    if (!alive || !props.canEdit || state.linking || !availableFiles().some((candidate) => candidate.id === file.id))
      return
    setState({ linking: file.id, error: "" })
    try {
      const result = await (selected.projectID === null
        ? linkCompanyKnowledge(selected.companyID, file.id)
        : linkProjectKnowledge(selected.projectID, file.id, selected.companyID))
      if (!isCurrent(selected)) return
      if (!result?.knowledge_id || !result.file_id || !knowledgeMatchesScope(result, selected)) throw new Error()
      acknowledge(selected, result.knowledge_id, result)
      removeAvailableFile(selected, result.file_id)
      closeAddDialog()
    } catch {
      if (isCurrent(selected)) setState("error", language.t("cowork.enterprise.knowledge.linkError"))
    } finally {
      if (isCurrent(selected)) setState("linking", "")
    }
  }

  const remove = async () => {
    const selected = scope()
    const item = state.removeTarget
    if (!isCurrent(selected) || !props.canEdit || !item || state.removing || !knowledgeMatchesScope(item, selected))
      return
    setState({ removing: item.knowledge_id, error: "" })
    try {
      if (selected.projectID === null) await removeCompanyKnowledge(selected.companyID, item.knowledge_id)
      else await removeProjectKnowledge(selected.projectID, item.knowledge_id, selected.companyID)
      if (!isCurrent(selected)) return
      acknowledge(selected, item.knowledge_id)
      removeAvailableFile(selected, item.file_id)
      setState("removeTarget", undefined)
      focusKnowledgePrimary()
    } catch {
      if (isCurrent(selected)) setState("error", language.t("cowork.enterprise.knowledge.deleteError"))
    } finally {
      if (isCurrent(selected)) setState("removing", "")
    }
  }

  return (
    <section class="overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base">
      <header class="flex flex-col gap-4 border-b border-v2-border-border-base px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 ref={knowledgeHeading} tabindex={-1} class="text-[16px] font-semibold">
            {language.t("cowork.enterprise.knowledge.title")}
          </h2>
          <p class="mt-1 max-w-2xl text-[11px] leading-5 text-v2-text-text-muted">
            {language.t(
              scope().projectID === null
                ? "cowork.enterprise.knowledge.companyDescription"
                : "cowork.enterprise.knowledge.description",
            )}
          </p>
        </div>
        <Show when={props.canEdit}>
          <div class="flex flex-wrap gap-2">
            <button
              hidden
              type="button"
              class="rounded-lg border border-v2-border-border-base px-3 py-2 text-[11px] font-semibold"
              onClick={(event) => {
                addDialogTrigger = event.currentTarget
                setState({ addOpen: true, error: "" })
              }}
            >
              {language.t("cowork.enterprise.knowledge.addExisting")}
            </button>
            <button
              ref={primaryAction}
              type="button"
              class="rounded-lg bg-v2-background-bg-contrast px-3 py-2 text-[11px] font-semibold text-v2-text-text-contrast"
              onClick={() => {
                pickerScope = scope()
                picker?.click()
              }}
            >
              {language.t("cowork.enterprise.knowledge.upload")}
            </button>
            <input
              ref={picker}
              class="hidden"
              type="file"
              multiple
              aria-label={language.t("cowork.enterprise.knowledge.upload")}
              onChange={(event) => {
                const selectedFiles = Array.from(event.currentTarget.files ?? [])
                event.currentTarget.value = ""
                const selectedScope = pickerScope
                pickerScope = undefined
                if (!selectedScope || !isCurrent(selectedScope) || !props.canEdit) return
                for (const file of selectedFiles) {
                  const item: UploadItem = {
                    id: nextUploadID++,
                    file,
                    scope: selectedScope,
                    status: "pending",
                  }
                  setUploads(uploads.length, item)
                  void submitUpload(item)
                }
              }}
            />
          </div>
        </Show>
      </header>

      <Show when={scope()} keyed>
        {(_selected) => <KnowledgeGuidance />}
      </Show>

      <Show when={state.error}>
        <p
          role="alert"
          class="border-b border-v2-border-border-base bg-v2-state-bg-danger px-5 py-3 text-[11px] text-v2-state-fg-danger"
        >
          {state.error}
        </p>
      </Show>
      <Show when={uploads.length > 0}>
        <ul class="space-y-2 border-b border-v2-border-border-base p-4" aria-live="polite">
          <For each={uploads}>
            {(item) => (
              <li class="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-v2-background-bg-layer-01 px-3 py-2 text-[11px]">
                <div class="min-w-0 flex-1">
                  <div class="truncate font-medium">{item.file.name}</div>
                  <div
                    classList={{
                      "text-v2-state-fg-danger": item.status === "error",
                      "text-v2-text-text-muted": item.status !== "error",
                    }}
                  >
                    {language.t(
                      item.status === "error"
                        ? "cowork.enterprise.knowledge.uploadError"
                        : "cowork.enterprise.knowledge.uploading",
                    )}
                  </div>
                </div>
                <div class="flex gap-3">
                  <Show when={item.status === "error"}>
                    <button type="button" class="font-medium" onClick={() => void submitUpload(item)}>
                      {language.t("cowork.enterprise.knowledge.retry")}
                    </button>
                  </Show>
                  <button
                    type="button"
                    class="font-medium"
                    onClick={() => setUploads((items) => items.filter((entry) => entry.id !== item.id))}
                  >
                    {language.t("cowork.enterprise.knowledge.removeUpload")}
                  </button>
                </div>
              </li>
            )}
          </For>
        </ul>
      </Show>

      <Show when={knowledge.error}>
        <EmptyState
          title={language.t("cowork.enterprise.knowledge.loadError")}
          action={language.t("cowork.enterprise.knowledge.retry")}
          onAction={() => void knowledgeActions.refetch()}
        />
      </Show>
      <Show when={!knowledge.error || knowledgeItems().length > 0}>
        <Show
          when={knowledgeItems().length > 0}
          fallback={
            <EmptyState
              loading={knowledge.loading}
              title={language.t("cowork.enterprise.knowledge.emptyTitle")}
              description={language.t(
                scope().projectID === null
                  ? "cowork.enterprise.knowledge.companyEmptyDescription"
                  : "cowork.enterprise.knowledge.emptyDescription",
              )}
            />
          }
        >
          <ul class="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3">
            <For each={knowledgeItems()}>
              {(item) => (
                <li class="flex min-w-0 flex-col rounded-xl border border-v2-border-border-muted p-4">
                  <div class="flex min-w-0 items-start gap-3">
                    <FileIcon node={{ path: item.filename, type: "file" }} class="size-8 shrink-0" />
                    <div class="min-w-0">
                      <div class="truncate text-[12px] font-semibold" title={item.filename}>
                        {item.filename}
                      </div>
                      <div class="mt-1 break-words text-[10px] text-v2-text-text-muted">{item.content_type}</div>
                      <span class="mt-2 inline-block rounded-md bg-v2-background-bg-layer-03 px-2 py-1 text-[10px] font-medium">
                        {language.t(item.project_id === null ? "cowork.company.label" : "cowork.enterprise.scope.project")}
                      </span>
                    </div>
                  </div>
                  <dl class="mt-4 grid grid-cols-2 gap-3 text-[10px] text-v2-text-text-muted">
                    <div>
                      <dt>{language.t("cowork.enterprise.knowledge.size")}</dt>
                      <dd class="mt-0.5 text-[11px] text-v2-text-text-base">{formatBytes(item.size)}</dd>
                    </div>
                    <div>
                      <dt>{language.t("cowork.enterprise.knowledge.uploaded")}</dt>
                      <dd class="mt-0.5 text-[11px] text-v2-text-text-base">{formatDate(item.created_at)}</dd>
                    </div>
                  </dl>
                  <div class="mt-4 flex min-h-11 items-center justify-end gap-2 border-t border-v2-border-border-muted pt-3">
                    <IconButtonV2
                      type="button"
                      size="large"
                      variant="ghost-muted"
                      class="box-content !p-2"
                      icon={<IconV2 name="download" size="small" />}
                      aria-label={language.t("cowork.enterprise.knowledge.download")}
                      title={language.t("cowork.enterprise.knowledge.download")}
                      onClick={() => void download(item)}
                    />
                    <Show when={props.canEdit && knowledgeMatchesScope(item, scope())}>
                      <IconButtonV2
                        type="button"
                        size="large"
                        variant="ghost-muted"
                        class="box-content !p-2"
                        icon={<IconV2 name="trash" size="small" class="text-v2-state-fg-danger" />}
                        aria-label={language.t("cowork.enterprise.knowledge.delete")}
                        title={language.t("cowork.enterprise.knowledge.delete")}
                        onClick={(event) => {
                          removeDialogTrigger = event.currentTarget
                          setState("removeTarget", item)
                        }}
                      />
                    </Show>
                  </div>
                </li>
              )}
            </For>
          </ul>
        </Show>
      </Show>

      <Show when={state.addOpen}>
        <div class="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={closeAddDialog}>
          <section
            ref={addDialog}
            class="flex max-h-[min(80vh,640px)] w-full max-w-xl flex-col overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="knowledge-existing-title"
            onClick={(event) => event.stopPropagation()}
            onKeyDown={(event) => handleDialogKeyDown(event, addDialog, closeAddDialog)}
          >
            <header class="flex shrink-0 items-center justify-between border-b border-v2-border-border-base px-5 py-4">
              <h2 id="knowledge-existing-title" class="text-[16px] font-semibold">
                {language.t("cowork.enterprise.knowledge.addExisting")}
              </h2>
              <button type="button" class="text-[11px]" onClick={closeAddDialog}>
                {language.t("cowork.enterprise.knowledge.close")}
              </button>
            </header>
            <Show when={scope().projectID === null}>
              <p class="shrink-0 px-5 py-3 text-[11px] leading-5 text-v2-text-text-muted">
                {language.t("cowork.enterprise.knowledge.companyLinkHint")}
              </p>
            </Show>
            <Show when={state.error}>
              <p role="alert" class="bg-v2-state-bg-danger px-5 py-3 text-[11px] text-v2-state-fg-danger">
                {state.error}
              </p>
            </Show>
            <Show
              when={!projectFiles.error}
              fallback={
                <EmptyState
                  title={language.t(
                    scope().projectID === null
                      ? "cowork.enterprise.knowledge.companyFilesError"
                      : "cowork.enterprise.knowledge.filesError",
                  )}
                  action={language.t("cowork.enterprise.knowledge.retry")}
                  onAction={() => void projectFileActions.refetch()}
                />
              }
            >
              <Show
                when={availableFiles().length > 0}
                fallback={
                  <EmptyState
                    loading={projectFiles.loading}
                    title={language.t(
                      scope().projectID === null
                        ? "cowork.enterprise.knowledge.companyNoFiles"
                        : "cowork.enterprise.knowledge.noFiles",
                    )}
                  />
                }
              >
                <ul class="min-h-0 overflow-y-auto p-3">
                  <For each={availableFiles()}>
                    {(file) => (
                      <li class="flex min-w-0 items-center gap-3 border-b border-v2-border-border-muted px-2 py-3 last:border-0">
                        <FileIcon node={{ path: file.filename, type: "file" }} class="size-7 shrink-0" />
                        <div class="min-w-0 flex-1">
                          <div class="truncate text-[12px] font-medium">{file.filename}</div>
                          <div class="text-[10px] text-v2-text-text-muted">{formatBytes(file.size)}</div>
                        </div>
                        <button
                          type="button"
                          disabled={!props.canEdit || !!state.linking}
                          class="shrink-0 rounded-lg border border-v2-border-border-base px-3 py-2 text-[11px] font-medium disabled:opacity-50"
                          onClick={() => void linkFile(file)}
                        >
                          {language.t(
                            state.linking === file.id
                              ? "cowork.enterprise.knowledge.linking"
                              : "cowork.enterprise.knowledge.add",
                          )}
                        </button>
                      </li>
                    )}
                  </For>
                </ul>
              </Show>
            </Show>
          </section>
        </div>
      </Show>

      <Show when={state.removeTarget}>
        {(item) => (
          <div class="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={closeRemoveDialog}>
            <section
              ref={removeDialog}
              class="w-full max-w-md rounded-xl border border-v2-border-border-base bg-v2-background-bg-base p-5 shadow-2xl"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="knowledge-remove-title"
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => handleDialogKeyDown(event, removeDialog, closeRemoveDialog)}
            >
              <h2 id="knowledge-remove-title" class="text-[16px] font-semibold">
                {language.t("cowork.enterprise.knowledge.deleteTitle")}
              </h2>
              <p class="mt-2 text-[12px] leading-5 text-v2-text-text-muted">
                {language.t(
                  scope().projectID === null
                    ? "cowork.enterprise.knowledge.companyDeleteDescription"
                    : "cowork.enterprise.knowledge.deleteDescription",
                  { name: item().filename },
                )}
              </p>
              <Show when={state.error}>
                <p role="alert" class="mt-3 text-[11px] text-v2-state-fg-danger">
                  {state.error}
                </p>
              </Show>
              <div class="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  disabled={!!state.removing}
                  class="rounded-lg px-3 py-2 text-[11px] font-medium disabled:opacity-50"
                  onClick={closeRemoveDialog}
                >
                  {language.t("cowork.enterprise.knowledge.cancel")}
                </button>
                <button
                  type="button"
                  disabled={!props.canEdit || !!state.removing}
                  class="rounded-lg bg-v2-state-bg-danger px-3 py-2 text-[11px] font-semibold text-v2-state-fg-danger disabled:opacity-50"
                  onClick={() => void remove()}
                >
                  {language.t(
                    state.removing
                      ? "cowork.enterprise.knowledge.deleting"
                      : "cowork.enterprise.knowledge.confirmDelete",
                  )}
                </button>
              </div>
            </section>
          </div>
        )}
      </Show>
    </section>
  )
}

function focusableElements(root: HTMLElement | undefined) {
  if (!root) return []
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  ).filter((element) => !element.hidden)
}

function focusFirst(root: HTMLElement | undefined) {
  focusableElements(root)[0]?.focus()
}

function handleDialogKeyDown(event: KeyboardEvent, root: HTMLElement | undefined, close: () => void) {
  if (event.key === "Escape") {
    event.preventDefault()
    close()
    return
  }
  if (event.key !== "Tab") return
  const elements = focusableElements(root)
  if (!elements.length) return
  const first = elements[0]
  const last = elements[elements.length - 1]
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

function EmptyState(props: {
  loading?: boolean
  title: string
  description?: string
  action?: string
  onAction?: () => void
}) {
  const language = useLanguage()
  return (
    <div class="grid min-h-52 place-items-center p-8 text-center">
      <Show
        when={!props.loading}
        fallback={
          <span
            role="status"
            aria-label={language.t("common.loading")}
            class="size-5 animate-spin rounded-full border-2 border-v2-border-border-base border-t-v2-icon-icon-base"
          />
        }
      >
        <div>
          <h3 class="text-[14px] font-semibold">{props.title}</h3>
          <Show when={props.description}>
            <p class="mx-auto mt-1 max-w-md text-[11px] leading-5 text-v2-text-text-muted">{props.description}</p>
          </Show>
          <Show when={props.action && props.onAction}>
            <button type="button" class="mt-3 text-[11px] font-medium" onClick={props.onAction}>
              {props.action}
            </button>
          </Show>
        </div>
      </Show>
    </div>
  )
}

export function knowledgeMatchesScope(item: CoworkKnowledge, scope: Pick<KnowledgeScope, "companyID" | "projectID">) {
  return item.company_id === scope.companyID && item.project_id === scope.projectID
}

export function knowledgeAvailableInScope(item: CoworkKnowledge, scope: Pick<KnowledgeScope, "companyID" | "projectID">) {
  return item.company_id === scope.companyID && (item.project_id === scope.projectID || item.project_id === null)
}

export function isKnowledgeCandidate(
  file: CoworkPortfolioFile,
  scope: Pick<KnowledgeScope, "companyID" | "projectID">,
  projects: CoworkProject[],
) {
  if (
    file.company_id !== scope.companyID ||
    !file.project_id ||
    file.period_id ||
    file.workflow_id ||
    file.session_id ||
    file.source === "session"
  )
    return false
  if (scope.projectID !== null) return file.project_id === scope.projectID
  return projects.some(
    (project) =>
      project.id === file.project_id &&
      project.company_id === scope.companyID &&
      (project.project_role === "owner" || project.project_role === "editor"),
  )
}

export function formatKnowledgeBytes(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "0 B"
  const units = ["B", "KB", "MB", "GB"]
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
  return `${(value / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`
}

export function upsertKnowledgeItem(items: CoworkKnowledge[], result: CoworkKnowledge) {
  const index = items.findIndex((item) => item.knowledge_id === result.knowledge_id)
  if (index === -1) return [...items, result]
  return items.flatMap((item, itemIndex) =>
    item.knowledge_id !== result.knowledge_id ? [item] : itemIndex === index ? [result] : [],
  )
}

export function removeKnowledgeItem(items: CoworkKnowledge[], knowledgeID: string) {
  return items.filter((item) => item.knowledge_id !== knowledgeID)
}

function formatBytes(value: number) {
  return formatKnowledgeBytes(value)
}

function formatDate(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric" }).format(date)
}
